// ======================================================================
// FILE:
// src/modules/finance/transactions/payment_voucher/pages/PaymentVoucherListPage.js
//
// MODULE:
// Finance & Accounts Management System (FBAMS)
//
// PAGE:
// Payment Voucher List
//
// PURPOSE:
// Displays Finance Payment Voucher transactions.
//
// FEATURES:
// - KPI Summary
// - Search & Filters
// - Payment Voucher Grid
// - Workflow Actions
// - Responsive layout
//
// IMPORTANT:
// - Uses existing Finance shared components
// - Uses existing financeLookupService
// - Uses paymentVoucherService
// - No Redux
// - No accounting logic
// ======================================================================

import React, {
    useEffect,
    useMemo,
    useState,
} from "react";

import {
    useNavigate,
} from "react-router-dom";

import AsyncSelect
    from "react-select/async";

import Select
    from "react-select";


// ======================================================================
// FINANCE COMMON COMPONENTS
// ======================================================================

import FinancePageHeader
    from "../../../components/FinancePageHeader";

import FinanceSummaryCards
    from "../../../components/FinanceSummaryCards";

import FinanceSearchPanel
    from "../../../components/FinanceSearchPanel";

import FinanceStatusBadge
    from "../../../components/FinanceStatusBadge";

import FinanceActionButtons
    from "../../../components/FinanceActionButtons";


// ======================================================================
// SERVICES
// ======================================================================

import paymentVoucherService
    from "../services/paymentVoucherService";

import financeLookupService
    from "../../../services/financeLookupService";


// ======================================================================
// COMPONENT
// ======================================================================

export default function PaymentVoucherListPage() {

    const navigate =
        useNavigate();


    // ==================================================================
    // LOADING
    // ==================================================================

    const [
        loading,
        setLoading
    ] = useState(false);


    // ==================================================================
    // DATA
    // ==================================================================

    const [
        vouchers,
        setVouchers
    ] = useState([]);


    // ==================================================================
    // ERROR
    // ==================================================================

    const [
        errorMessage,
        setErrorMessage
    ] = useState("");


    // ==================================================================
    // COMPANY
    // ==================================================================

    const [
        selectedCompany,
        setSelectedCompany
    ] = useState(null);


    // ==================================================================
    // FILTERS
    // ==================================================================

    const [
        filters,
        setFilters
    ] = useState({

        company_id:
            null,

        search:
            "",

        status:
            "",

        payee_type:
            "",

        payment_mode:
            "",

        date_from:
            "",

        date_to:
            "",

    });


    // ==================================================================
    // PAYEE TYPE OPTIONS
    // ==================================================================

    const payeeTypeOptions =
        useMemo(
            () => [

                {
                    value: "",
                    label: "All",
                },

                {
                    value: "BUSINESS_PARTNER",
                    label: "Business Partner",
                },

                {
                    value: "VENDOR",
                    label: "Vendor",
                },

                {
                    value: "CUSTOMER",
                    label: "Customer",
                },

                {
                    value: "EMPLOYEE",
                    label: "Employee",
                },

                {
                    value: "GOVERNMENT",
                    label: "Government / Tax Authority",
                },

                {
                    value: "OTHER",
                    label: "Other",
                },

            ],
            []
        );


    // ==================================================================
    // PAYMENT MODE OPTIONS
    // ==================================================================

    const paymentModeOptions =
        useMemo(
            () => [

                {
                    value: "",
                    label: "All",
                },

                {
                    value: "BANK_TRANSFER",
                    label: "Bank Transfer",
                },

                {
                    value: "NEFT",
                    label: "NEFT",
                },

                {
                    value: "RTGS",
                    label: "RTGS",
                },

                {
                    value: "IMPS",
                    label: "IMPS",
                },

                {
                    value: "CHEQUE",
                    label: "Cheque",
                },

                {
                    value: "CASH",
                    label: "Cash",
                },

                {
                    value: "OTHER",
                    label: "Other",
                },

            ],
            []
        );


    // ==================================================================
    // STATUS OPTIONS
    // ==================================================================

    const statusOptions =
        useMemo(
            () => [

                {
                    value: "",
                    label: "All",
                },

                {
                    value: "DRAFT",
                    label: "Draft",
                },

                {
                    value: "SUBMITTED",
                    label: "Submitted",
                },

                {
                    value: "APPROVED",
                    label: "Approved",
                },

                {
                    value: "POSTED",
                    label: "Posted",
                },

                {
                    value: "CANCELLED",
                    label: "Cancelled",
                },

            ],
            []
        );


    // ==================================================================
    // LOAD VOUCHERS
    // ==================================================================

    useEffect(
        () => {

            /*
             * Payment Voucher list API requires company_id.
             *
             * Therefore we intentionally do not call the API until
             * a company has been selected.
             */

            if (
                !filters.company_id
            ) {

                setVouchers([]);

                return;

            }

            loadPaymentVouchers(
                filters
            );

        },
        [
            filters.company_id
        ]
    );


    // ==================================================================
    // LOAD PAYMENT VOUCHERS
    // ==================================================================

    async function loadPaymentVouchers(
        appliedFilters = filters
    ) {

        if (
            !appliedFilters.company_id
        ) {

            setVouchers([]);

            return;

        }


        try {

            setLoading(true);

            setErrorMessage("");


            const result =
                await paymentVoucherService
                    .listPaymentVouchers(
                        {

                            company_id:
                                appliedFilters.company_id,

                            search:
                                appliedFilters.search ||
                                undefined,

                            status:
                                appliedFilters.status ||
                                undefined,

                            payee_type:
                                appliedFilters.payee_type ||
                                undefined,

                            payment_mode:
                                appliedFilters.payment_mode ||
                                undefined,

                            date_from:
                                appliedFilters.date_from ||
                                undefined,

                            date_to:
                                appliedFilters.date_to ||
                                undefined,

                        }
                    );


            setVouchers(
                Array.isArray(
                    result.rows
                )
                    ? result.rows
                    : []
            );

        }

        catch (error) {

            console.error(
                "Failed to load Payment Vouchers:",
                error
            );


            setVouchers([]);


            setErrorMessage(
                error?.response?.data?.message ||
                error?.message ||
                "Failed to load Payment Vouchers."
            );

        }

        finally {

            setLoading(false);

        }

    }


    // ==================================================================
    // UPDATE FILTER
    // ==================================================================

    function updateFilter(
        field,
        value
    ) {

        setFilters(
            previous => ({

                ...previous,

                [field]:
                    value,

            })
        );

    }


    // ==================================================================
    // SEARCH
    // ==================================================================

    function handleSearch() {

        if (
            !filters.company_id
        ) {

            setErrorMessage(
                "Please select a Company."
            );

            return;

        }


        loadPaymentVouchers(
            filters
        );

    }


    // ==================================================================
    // RESET
    // ==================================================================

    function handleReset() {

        const resetFilters = {

            company_id:
                selectedCompany?.value ||
                null,

            search:
                "",

            status:
                "",

            payee_type:
                "",

            payment_mode:
                "",

            date_from:
                "",

            date_to:
                "",

        };


        setFilters(
            resetFilters
        );


        setErrorMessage("");


        if (
            resetFilters.company_id
        ) {

            loadPaymentVouchers(
                resetFilters
            );

        }

        else {

            setVouchers([]);

        }

    }


    // ==================================================================
    // NAVIGATION
    // ==================================================================

    function handleCreate() {

        navigate(
            "/finance/payment-vouchers/create"
        );

    }


    function handleView(
        row
    ) {

        navigate(
            `/finance/payment-vouchers/view/${row.id}`
        );

    }


    function handleEdit(
        row
    ) {

        navigate(
            `/finance/payment-vouchers/edit/${row.id}`
        );

    }


    // ==================================================================
    // DELETE
    // ==================================================================

    async function handleDelete(
        row
    ) {

        if (
            !window.confirm(
                "Delete selected Payment Voucher?"
            )
        ) {

            return;

        }


        try {

            setErrorMessage("");


            await paymentVoucherService
                .deletePaymentVoucher(
                    row.id
                );


            loadPaymentVouchers(
                filters
            );

        }

        catch (error) {

            console.error(
                "Failed to delete Payment Voucher:",
                error
            );


            setErrorMessage(
                error?.response?.data?.message ||
                error?.message ||
                "Failed to delete Payment Voucher."
            );

        }

    }


    // ==================================================================
    // SUBMIT
    // ==================================================================

    async function handleSubmit(
        row
    ) {

        try {

            setErrorMessage("");

            await paymentVoucherService
                .submitPaymentVoucher(
                    row.id,
                    filters.company_id
                );

            loadPaymentVouchers(
                filters
            );

        }
        catch (
            error
        ) {

            console.error(
                "Failed to submit Payment Voucher:",
                error
            );

            setErrorMessage(
                error?.response?.data?.message ||
                error?.message ||
                "Failed to submit Payment Voucher."
            );

        }

    }


    // ==================================================================
    // APPROVE
    // ==================================================================

    async function handleApprove(
        row
    ) {

        try {

            setErrorMessage("");


            await paymentVoucherService
                .approvePaymentVoucher(
                    row.id
                );


            loadPaymentVouchers(
                filters
            );

        }

        catch (error) {

            console.error(
                "Failed to approve Payment Voucher:",
                error
            );


            setErrorMessage(
                error?.response?.data?.message ||
                error?.message ||
                "Failed to approve Payment Voucher."
            );

        }

    }


    // ==================================================================
    // POST
    // ==================================================================

    async function handlePost(
        row
    ) {

        try {

            setErrorMessage("");


            await paymentVoucherService
                .postPaymentVoucher(
                    row.id
                );


            loadPaymentVouchers(
                filters
            );

        }

        catch (error) {

            console.error(
                "Failed to post Payment Voucher:",
                error
            );


            setErrorMessage(
                error?.response?.data?.message ||
                error?.message ||
                "Failed to post Payment Voucher."
            );

        }

    }


    // ==================================================================
    // ASYNC COMPANY LOADER
    // ==================================================================

    async function loadCompanies(
        inputValue
    ) {

        try {

            const response =
                await financeLookupService
                    .searchCompanies(
                        inputValue
                    );


            return (
                response || []
            ).map(
                company => ({

                    value:
                        company.id,

                    label:
                        company.company_name,

                })
            );

        }

        catch {

            return [];

        }

    }


    // ==================================================================
    // SUMMARY
    // ==================================================================

    const summary =
        useMemo(
            () => {

                const result = {

                    total:
                        vouchers.length,

                    draft:
                        0,

                    submitted:
                        0,

                    approved:
                        0,

                    posted:
                        0,

                    cancelled:
                        0,

                };


                vouchers.forEach(
                    voucher => {

                        const status =
                            String(
                                voucher.status ||
                                ""
                            ).toUpperCase();


                        if (
                            status === "DRAFT"
                        ) {

                            result.draft += 1;

                        }

                        else if (
                            status === "SUBMITTED"
                        ) {

                            result.submitted += 1;

                        }

                        else if (
                            status === "APPROVED"
                        ) {

                            result.approved += 1;

                        }

                        else if (
                            status === "POSTED"
                        ) {

                            result.posted += 1;

                        }

                        else if (
                            status === "CANCELLED"
                        ) {

                            result.cancelled += 1;

                        }

                    }
                );


                return result;

            },
            [
                vouchers
            ]
        );


    // ==================================================================
    // SUMMARY CARDS
    // ==================================================================

    const summaryCards = [

        {
            key:
                "total",

            title:
                "Total Vouchers",

            value:
                summary.total,

            icon:
                "fas fa-money-check-alt",

            background:
                "#0d6efd",
        },

        {
            key:
                "draft",

            title:
                "Draft",

            value:
                summary.draft,

            icon:
                "fas fa-pencil-alt",

            background:
                "#6c757d",
        },

        {
            key:
                "submitted",

            title:
                "Submitted",

            value:
                summary.submitted,

            icon:
                "fas fa-paper-plane",

            background:
                "#ffc107",
        },

        {
            key:
                "approved",

            title:
                "Approved",

            value:
                summary.approved,

            icon:
                "fas fa-check",

            background:
                "#198754",
        },

        {
            key:
                "posted",

            title:
                "Posted",

            value:
                summary.posted,

            icon:
                "fas fa-check-double",

            background:
                "#0dcaf0",
        },

    ];


    // ==================================================================
    // RENDER
    // ==================================================================

    return (

        <>

            {/* ==========================================================
                PAGE HEADER
            ========================================================== */}

            <FinancePageHeader

                icon="fas fa-money-check-alt"

                title="Payment Voucher"

                subtitle={
                    "Create and manage Finance payment vouchers."
                }

                breadcrumbs={[

                    {
                        label:
                            "Finance",

                        to:
                            "/finance",
                    },

                    {
                        label:
                            "Payment Voucher",
                    },

                ]}

                actions={

                    <button

                        className="btn btn-primary"

                        onClick={
                            handleCreate
                        }

                    >

                        <i
                            className="
                                fas
                                fa-plus
                                me-2
                            "
                        />

                        New Payment Voucher

                    </button>

                }

            />


            {/* ==========================================================
                SUMMARY
            ========================================================== */}

            <FinanceSummaryCards

                cards={
                    summaryCards
                }

            />


            {/* ==========================================================
                SEARCH / FILTERS
            ========================================================== */}

            <FinanceSearchPanel

                loading={
                    loading
                }

                onSearch={
                    handleSearch
                }

                onReset={
                    handleReset
                }

            >

                {/* ======================================================
                    COMPANY
                ====================================================== */}

                <div
                    className="
                        col-lg-3
                        col-md-6
                    "
                >

                    <label
                        className="form-label"
                    >
                        Company
                    </label>


                    <AsyncSelect

                        cacheOptions

                        defaultOptions

                        loadOptions={
                            loadCompanies
                        }

                        isClearable

                        value={
                            selectedCompany
                        }

                        onChange={
                            option => {

                                setSelectedCompany(
                                    option
                                );


                                updateFilter(
                                    "company_id",
                                    option?.value ||
                                    null
                                );

                            }
                        }

                    />

                </div>


                {/* ======================================================
                    SEARCH
                ====================================================== */}

                <div
                    className="
                        col-lg-3
                        col-md-6
                    "
                >

                    <label
                        className="form-label"
                    >
                        Search
                    </label>


                    <input

                        className="form-control"

                        value={
                            filters.search
                        }

                        onChange={
                            event =>
                                updateFilter(
                                    "search",
                                    event.target.value
                                )
                        }

                        placeholder="
                            Voucher No / Payee / Reference
                        "

                    />

                </div>


                {/* ======================================================
                    STATUS
                ====================================================== */}

                <div
                    className="
                        col-lg-2
                        col-md-6
                    "
                >

                    <label
                        className="form-label"
                    >
                        Status
                    </label>


                    <Select

                        options={
                            statusOptions
                        }

                        value={
                            statusOptions.find(
                                option =>
                                    option.value ===
                                    filters.status
                            )
                        }

                        onChange={
                            option =>
                                updateFilter(
                                    "status",
                                    option?.value ||
                                    ""
                                )
                        }

                    />

                </div>


                {/* ======================================================
                    PAYEE TYPE
                ====================================================== */}

                <div
                    className="
                        col-lg-2
                        col-md-6
                    "
                >

                    <label
                        className="form-label"
                    >
                        Payee Type
                    </label>


                    <Select

                        options={
                            payeeTypeOptions
                        }

                        value={
                            payeeTypeOptions.find(
                                option =>
                                    option.value ===
                                    filters.payee_type
                            )
                        }

                        onChange={
                            option =>
                                updateFilter(
                                    "payee_type",
                                    option?.value ||
                                    ""
                                )
                        }

                    />

                </div>


                {/* ======================================================
                    PAYMENT MODE
                ====================================================== */}

                <div
                    className="
                        col-lg-2
                        col-md-6
                    "
                >

                    <label
                        className="form-label"
                    >
                        Payment Mode
                    </label>


                    <Select

                        options={
                            paymentModeOptions
                        }

                        value={
                            paymentModeOptions.find(
                                option =>
                                    option.value ===
                                    filters.payment_mode
                            )
                        }

                        onChange={
                            option =>
                                updateFilter(
                                    "payment_mode",
                                    option?.value ||
                                    ""
                                )
                        }

                    />

                </div>


                {/* ======================================================
                    DATE FROM
                ====================================================== */}

                <div
                    className="
                        col-lg-2
                        col-md-6
                    "
                >

                    <label
                        className="form-label"
                    >
                        Date From
                    </label>


                    <input

                        type="date"

                        className="form-control"

                        value={
                            filters.date_from
                        }

                        onChange={
                            event =>
                                updateFilter(
                                    "date_from",
                                    event.target.value
                                )
                        }

                    />

                </div>


                {/* ======================================================
                    DATE TO
                ====================================================== */}

                <div
                    className="
                        col-lg-2
                        col-md-6
                    "
                >

                    <label
                        className="form-label"
                    >
                        Date To
                    </label>


                    <input

                        type="date"

                        className="form-control"

                        value={
                            filters.date_to
                        }

                        onChange={
                            event =>
                                updateFilter(
                                    "date_to",
                                    event.target.value
                                )
                        }

                    />

                </div>

            </FinanceSearchPanel>


            {/* ==========================================================
                ERROR
            ========================================================== */}

            {
                errorMessage && (

                    <div
                        className="
                            alert
                            alert-danger
                            mt-3
                        "
                    >

                        {errorMessage}

                    </div>

                )
            }


            {/* ==========================================================
                PAYMENT VOUCHER GRID
            ========================================================== */}

            <div
                className="
                    card
                    shadow-sm
                    border-0
                "
            >

                <div
                    className="card-body p-0"
                >

                    <div
                        className="table-responsive"
                    >

                        <table
                            className="
                                table
                                table-hover
                                align-middle
                                mb-0
                            "
                        >

                            <thead
                                className="table-light"
                            >

                                <tr>

                                    <th
                                        style={{
                                            width: 60,
                                        }}
                                    >
                                        #
                                    </th>

                                    <th>
                                        Voucher No
                                    </th>

                                    <th>
                                        Date
                                    </th>

                                    <th>
                                        Payee
                                    </th>

                                    <th>
                                        Payee Type
                                    </th>

                                    <th>
                                        Payment Mode
                                    </th>

                                    <th
                                        className="text-end"
                                    >
                                        Amount
                                    </th>

                                    <th>
                                        Currency
                                    </th>

                                    <th>
                                        Status
                                    </th>

                                    <th>
                                        Approval
                                    </th>

                                    <th
                                        className="
                                            text-center
                                        "
                                        style={{
                                            width: 220,
                                        }}
                                    >
                                        Actions
                                    </th>

                                </tr>

                            </thead>


                            <tbody>

                                {
                                    loading ? (

                                        <tr>

                                            <td
                                                colSpan={11}
                                                className="
                                                    text-center
                                                    py-5
                                                "
                                            >

                                                <div
                                                    className="
                                                        spinner-border
                                                        text-primary
                                                    "
                                                    role="status"
                                                />

                                            </td>

                                        </tr>

                                    ) : !filters.company_id ? (

                                        <tr>

                                            <td
                                                colSpan={11}
                                                className="
                                                    text-center
                                                    py-5
                                                "
                                            >

                                                <div
                                                    className="
                                                        text-muted
                                                    "
                                                >

                                                    <i
                                                        className="
                                                            fas
                                                            fa-building
                                                            fa-3x
                                                            mb-3
                                                        "
                                                    />

                                                    <h5>
                                                        Select Company
                                                    </h5>

                                                    <p>
                                                        Select a Company
                                                        to view Payment
                                                        Vouchers.
                                                    </p>

                                                </div>

                                            </td>

                                        </tr>

                                    ) : vouchers.length === 0 ? (

                                        <tr>

                                            <td
                                                colSpan={11}
                                                className="
                                                    text-center
                                                    py-5
                                                "
                                            >

                                                <div
                                                    className="
                                                        text-muted
                                                    "
                                                >

                                                    <i
                                                        className="
                                                            fas
                                                            fa-money-check-alt
                                                            fa-3x
                                                            mb-3
                                                        "
                                                    />

                                                    <h5>
                                                        No Payment
                                                        Vouchers Found
                                                    </h5>

                                                    <p>
                                                        Create your first
                                                        Payment Voucher.
                                                    </p>

                                                </div>

                                            </td>

                                        </tr>

                                    ) : (

                                        vouchers.map(
                                            (
                                                row,
                                                index
                                            ) => (

                                                <tr
                                                    key={
                                                        row.id
                                                    }
                                                >

                                                    {/* ==================================
                                                        #
                                                    ================================== */}

                                                    <td>
                                                        {
                                                            index + 1
                                                        }
                                                    </td>


                                                    {/* ==================================
                                                        VOUCHER NO
                                                    ================================== */}

                                                    <td>

                                                        <button

                                                            className="
                                                                btn
                                                                btn-link
                                                                p-0
                                                            "

                                                            onClick={() =>
                                                                handleView(
                                                                    row
                                                                )
                                                            }

                                                        >

                                                            {
                                                                row.voucher_no ||
                                                                "-"
                                                            }

                                                        </button>

                                                    </td>


                                                    {/* ==================================
                                                        DATE
                                                    ================================== */}

                                                    <td>

                                                        {
                                                            row.voucher_date
                                                                ? String(
                                                                    row.voucher_date
                                                                ).slice(
                                                                    0,
                                                                    10
                                                                )
                                                                : "-"
                                                        }

                                                    </td>


                                                    {/* ==================================
                                                        PAYEE
                                                    ================================== */}

                                                    <td>

                                                        {
                                                            row.payee_name ||
                                                            "-"
                                                        }

                                                    </td>


                                                    {/* ==================================
                                                        PAYEE TYPE
                                                    ================================== */}

                                                    <td>

                                                        {
                                                            row.payee_type ||
                                                            "-"
                                                        }

                                                    </td>


                                                    {/* ==================================
                                                        PAYMENT MODE
                                                    ================================== */}

                                                    <td>

                                                        {
                                                            row.payment_mode ||
                                                            "-"
                                                        }

                                                    </td>


                                                    {/* ==================================
                                                        AMOUNT
                                                    ================================== */}

                                                    <td
                                                        className="
                                                            text-end
                                                        "
                                                    >

                                                        {
                                                            Number(
                                                                row.transaction_amount ||
                                                                0
                                                            ).toLocaleString(
                                                                "en-IN",
                                                                {
                                                                    minimumFractionDigits:
                                                                        2,

                                                                    maximumFractionDigits:
                                                                        2,
                                                                }
                                                            )
                                                        }

                                                    </td>


                                                    {/* ==================================
                                                        CURRENCY
                                                    ================================== */}

                                                    <td>

                                                        {
                                                            row.currency_code ||
                                                            "-"
                                                        }

                                                    </td>


                                                    {/* ==================================
                                                        STATUS
                                                    ================================== */}

                                                    <td>

                                                        <FinanceStatusBadge

                                                            type="journal"

                                                            value={
                                                                row.status
                                                            }

                                                        />

                                                    </td>


                                                    {/* ==================================
                                                        APPROVAL
                                                    ================================== */}

                                                    <td>

                                                        <FinanceStatusBadge

                                                            type="approval"

                                                            value={
                                                                row.approval_status
                                                            }

                                                        />

                                                    </td>


                                                    {/* ==================================
                                                        ACTIONS
                                                    ================================== */}

                                                    <td
                                                        className="
                                                            text-center
                                                        "
                                                    >

                                                        <FinanceActionButtons

                                                            row={
                                                                row
                                                            }

                                                            onView={
                                                                handleView
                                                            }

                                                            onEdit={
                                                                handleEdit
                                                            }

                                                            onDelete={
                                                                handleDelete
                                                            }

                                                            onSubmit={
                                                                handleSubmit
                                                            }

                                                            onApprove={
                                                                handleApprove
                                                            }

                                                            onPost={
                                                                handlePost
                                                            }

                                                        />

                                                    </td>

                                                </tr>

                                            )
                                        )

                                    )
                                }

                            </tbody>

                        </table>

                    </div>

                </div>

            </div>


            {/* ==========================================================
                RESULT COUNT
            ========================================================== */}

            <div
                className="
                    d-flex
                    justify-content-between
                    align-items-center
                    mt-4
                "
            >

                <small
                    className="text-muted"
                >

                    Total Records:

                    <strong
                        className="ms-1"
                    >
                        {
                            vouchers.length
                        }
                    </strong>

                </small>

            </div>

        </>

    );

}